import { useEffect, useMemo, useState } from "react";
import api from "../api";
import { CardIntro, Field, FileInput, Segmented } from "./ui/Form";
import AbundanceReadyNow from "./AbundanceReadyNow";
import AbundanceWatchStatus from "./AbundanceWatchStatus";

/**
 * Abundance Report data (ROADMAP B2, Amy -- the VectorSurv half).
 *
 * Separate from Recheck Report Data on purpose. Those three are pulled live
 * from FieldSeeker; VectorSurv has no API we can use, so these two are exports
 * Amy makes by hand and uploads. Keeping "pull" meaning pull is worth a
 * second card.
 *
 * Both CSVs are uploaded together even when only one is being pasted. The
 * check that matters compares them against each other: Access joins
 * Abundance to Surveillance Sites with an INNER JOIN on site_code, so a
 * collection whose site is missing is not an error -- it is absent, and the
 * report looks fine. Site 122011-810 went missing that way for six weeks and
 * took 8 records and 41 mosquitoes with it.
 */

type Dataset = "abundance" | "sites";

interface AddDay {
  day: string;
  rows: number;
  weeks: string[];
  by: string[];
  collection_days: { date: string; label: string; rows: number }[];
  last_add: string | null;
}

interface SiteDay {
  day: string;
  active: number;
  deactivated: number;
}

interface MissingSite {
  site_code: string;
  site_name: string;
  rows: number;
  first: string | null;
  last: string | null;
  weeks: string[];
}

interface Check {
  missing_sites: MissingSite[];
  duplicate_codes: { site_code: string; site_names: string[] }[];
  sites_in_export: number;
}

interface Collection {
  date: string;
  rows: number;
  first_add: string;
  last_add: string;
  hold: boolean;
  active: boolean;
  why: string | null;
}

interface Readiness {
  collections: Collection[];
  newest: string | null;
  latest_entry: string | null;
  ordered: boolean;
  overlaps: { older: string; newer: string }[];
}

interface DayTotal {
  day: string;
  rows: number;
  nights: Record<string, number>;
}

/** Access should hold every row collected on or before the latest collection
 *  night in the report: the narrowed night if one is picked, else the newest
 *  night inside the chosen add dates. */
export function expectedAccessTotal(
  totals: { date: string; rows: number }[] | undefined,
  nights: { date: string }[],
  narrowed: string[],
  pasted: number
): { pasted: number; total: number; newest: string } | null {
  const pool = narrowed.length > 0 ? narrowed : nights.map((n) => n.date);
  const newest = pool.reduce((m, d) => (d > m ? d : m), "");
  if (!newest || !totals) return null;
  const total = totals.filter((t) => t.date <= newest).reduce((s, t) => s + t.rows, 0);
  return { pasted, total, newest };
}

interface Inspection {
  collection_totals?: { date: string; rows: number }[];
  add_days: AddDay[];
  /** Every add date, oldest first -- the picker only shows the newest few. */
  all_days: DayTotal[];
  site_days: SiteDay[];
  readiness: Readiness;
  check: Check;
  counts: { abundance_rows: number; site_rows: number };
}

interface Prepared {
  table: string;
  columns: string[];
  rows: string[][];
  skipped: { site_name: string; why: string }[];
  unfilled: string[];
  check: Check;
}

interface LayerState {
  label: string;
  title?: string;
  row_count?: number;
  newest_data?: string | null;
  error?: string;
}

const DATASETS: [Dataset, string][] = [
  ["abundance", "Abundance"],
  ["sites", "Surveillance Sites"],
];

export default function AbundanceReportCard() {
  const [dataset, setDataset] = useState<Dataset>("abundance");
  const [abundanceFile, setAbundanceFile] = useState<File | null>(null);
  const [sitesFile, setSitesFile] = useState<File | null>(null);
  const [inspection, setInspection] = useState<Inspection | null>(null);
  const [abDays, setAbDays] = useState<string[]>([]);
  /** Collection nights to keep, within the chosen add dates. Empty means all
   *  of them, which is what it was before this existed. */
  const [collectionDays, setCollectionDays] = useState<string[]>([]);
  const [days, setDays] = useState<string[]>([]);
  const [prepared, setPrepared] = useState<Prepared | null>(null);
  // Which source the current inspection came from, so Prepare uses the same
  // one. Preparing from files what was inspected from Gateway would hand Amy
  // rows she never looked at.
  const [source, setSource] = useState<"files" | "vectorsurv">("files");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [layers, setLayers] = useState<Record<string, LayerState> | null>(null);

  // What the dashboard layers already hold. Two people load the same VectorSurv
  // export -- Salman to the dashboard, Amy to Access -- and neither could
  // otherwise see how far the other had got.
  useEffect(() => {
    api
      .get("/abundance-upload-state")
      .then((r) => setLayers(r.data.layers))
      .catch(() => {});
  }, []);

  /** The two tables, from disk or straight from Gateway.
   *
   * The sync sends no files and the backend builds the same two tables from
   * the VectorSurv API instead -- same columns, same order, same preparation
   * afterwards. Checked against a manual export of the same day: every cell
   * of the abundance paste matches except calculated_city_distance, which
   * Pioneer deliberately leaves empty.
   */
  const body = (fromVectorsurv = false) => {
    const fd = new FormData();
    if (fromVectorsurv) {
      fd.append("source", "vectorsurv");
      return fd;
    }
    fd.append("abundance", abundanceFile as File);
    fd.append("sites", sitesFile as File);
    return fd;
  };

  const inspect = async (fromVectorsurv = false) => {
    if (!fromVectorsurv && (!abundanceFile || !sitesFile)) return;
    setSource(fromVectorsurv ? "vectorsurv" : "files");
    setBusy(fromVectorsurv ? "sync" : "inspect");
    setError(null);
    setPrepared(null);
    try {
      const r = await api.post("/abundance-inspect", body(fromVectorsurv));
      setInspection(r.data);
      setAbDays([]);
      setDays([]);
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          (fromVectorsurv
            ? "Could not reach VectorSurv. The Gateway credential may have expired."
            : "Could not read those files.")
      );
    } finally {
      setBusy("");
    }
  };

  const prepare = async (asCsv = false) => {
    setBusy(asCsv ? "csv" : "prepare");
    setError(null);
    try {
      const fd = body(source === "vectorsurv");
      fd.append("dataset", dataset);
      // Both datasets select by date; only the list differs.
      (dataset === "abundance" ? abDays : days).forEach((d) => fd.append("days", d));
      if (dataset === "abundance") {
        collectionDays.forEach((d) => fd.append("collection_days", d));
      }
      if (asCsv) fd.append("format", "csv");
      const r = await api.post("/abundance-prepare", fd, {
        responseType: asCsv ? "blob" : "json",
      });
      if (asCsv) {
        const url = URL.createObjectURL(r.data as Blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${dataset}_prepared.csv`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        setPrepared(r.data);
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || "Could not prepare those rows.");
    } finally {
      setBusy("");
    }
  };

  /** Headerless TSV, because Access splits tabs across columns on paste and a
   *  header line would become a record. */
  const copy = async () => {
    if (!prepared) return;
    const cell = (v: string) => String(v ?? "").replace(/[\t\r\n]+/g, " ");
    const text = prepared.rows.map((r) => r.map(cell).join("\t")).join("\r\n");
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 5000);
    } catch {
      setError("The browser blocked the clipboard. Use the CSV download instead.");
    }
  };

  const ready = abundanceFile && sitesFile;
  const missing = inspection?.check.missing_sites ?? [];
  const dupes = inspection?.check.duplicate_codes ?? [];
  /** Every collection night inside the chosen add dates, with its row count
   *  summed across them. A night keyed in over two days appears once, with
   *  the total -- which is the thing that was invisible before: collection
   *  09-29 was entered on both 09-28 and 09-30, so picking one add date gave
   *  a report six rows short with nothing on screen to say so. */
  const nightsAvailable = useMemo(() => {
    const totals = new Map<string, { date: string; label: string; rows: number }>();
    (inspection?.all_days ?? [])
      .filter((d) => abDays.includes(d.day))
      .forEach((d) =>
        Object.entries(d.nights).forEach(([date, rows]) => {
          const seen = totals.get(date);
          totals.set(date, {
            date,
            label: `${date.slice(5, 7)}/${date.slice(8, 10)}`,
            rows: (seen?.rows ?? 0) + rows,
          });
        })
      );
    // Array.from rather than a spread: this project's TS target does not
    // allow iterating a Map without downlevelIteration.
    return Array.from(totals.values()).sort((a, b) => b.date.localeCompare(a.date));
  }, [inspection, abDays]);

  /** What Access should hold after this paste, for Amy to check against
   *  Access's own record count. The check that was missing: pasting a day
   *  out of order left whole collections absent from Access for months and
   *  nothing on screen could say so.
   *
   *  Counted by collection date, not add date: Access should hold every row
   *  collected on or before the latest collection night being reported. */
  const expected = useMemo(() => {
    if (dataset !== "abundance" || !inspection || abDays.length === 0) return null;
    const chosen = inspection.all_days.filter((d) => abDays.includes(d.day));
    const chosenRows = chosen.reduce((t, d) => t + d.rows, 0);
    const pasted =
      collectionDays.length === 0
        ? chosenRows
        : chosen.reduce(
            (t, d) =>
              t +
              collectionDays.reduce((n, c) => n + (d.nights[c] ?? 0), 0),
            0
          );
    return expectedAccessTotal(inspection.collection_totals, nightsAvailable, collectionDays, pasted);
  }, [dataset, inspection, abDays, collectionDays, nightsAvailable]);

  // Nights that are no longer on offer must not keep filtering silently.
  useEffect(() => {
    const live = new Set(nightsAvailable.map((n) => n.date));
    setCollectionDays((prev) => {
      const kept = prev.filter((d) => live.has(d));
      return kept.length === prev.length ? prev : kept;
    });
  }, [nightsAvailable]);

  const selected = dataset === "abundance" ? abDays.length : days.length;

  return (
    <div className="space-y-4">
      <CardIntro>
        The two VectorSurv exports, prepared for the Access paste. Upload both —
        the check for collections whose site is missing compares them against
        each other, and Access reports nothing when that happens.
      </CardIntro>

      <AbundanceReadyNow />

      {layers && (
        <div className="flex flex-wrap gap-2">
          {Object.entries(layers).map(([k, l]) => (
            <span key={k} className="pill bg-paper-2 text-ink-soft">
              {l.label}:{" "}
              {l.error
                ? "unavailable"
                : `${(l.row_count ?? 0).toLocaleString()} rows through ${
                    (l.newest_data || "").slice(0, 10) || "—"
                  }`}
            </span>
          ))}
          <span className="text-[12px] text-ink-faint self-center">
            already on the dashboard layers
          </span>
        </div>
      )}

      <div className="grid gap-3 min-[640px]:grid-cols-2">
        <Field label="Abundance CSV" hint="VectorSurv → Collections → Export">
          <FileInput
            accept=".csv,text/csv"
            file={abundanceFile}
            onFile={(f) => {
              setAbundanceFile(f);
              setInspection(null);
              setPrepared(null);
            }}
          />
        </Field>
        <Field label="Surveillance Sites CSV" hint="VectorSurv → Sites → Export">
          <FileInput
            accept=".csv,text/csv"
            file={sitesFile}
            onFile={(f) => {
              setSitesFile(f);
              setInspection(null);
              setPrepared(null);
            }}
          />
        </Field>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => inspect(false)}
          disabled={!ready || !!busy}
          className="btn-primary text-sm"
        >
          {busy === "inspect" ? "Reading…" : "Read both files"}
        </button>
        <span className="text-ink-faint text-[13px]">or</span>
        <button
          onClick={() => inspect(true)}
          disabled={!!busy}
          className="btn-ghost text-sm"
          title="Pull both tables straight from VectorSurv, with no download"
        >
          {busy === "sync" ? "Pulling from VectorSurv…" : "Sync from VectorSurv"}
        </button>
        {source === "vectorsurv" && inspection && (
          <span className="pill bg-brand-tint text-brand-deep">From VectorSurv</span>
        )}
      </div>
      {busy === "sync" && (
        <p className="text-[12px] text-ink-faint">
          Reading several thousand collections and every site. This takes a
          minute or so.
        </p>
      )}

      {error && (
        <div className="p-3 rounded-sm2 bg-wnv-tint text-wnv text-[13px]">{error}</div>
      )}

      {inspection && (
        <>
          {missing.length > 0 && (
            <div className="p-3 rounded-sm2 bg-wnv-tint text-wnv text-[13px]">
              <strong>
                {(() => {
                  const n = missing.reduce((a, m) => a + m.rows, 0);
                  return `${n} collection${n === 1 ? "" : "s"} ${
                    n === 1 ? "has" : "have"
                  } no matching site`;
                })()}
              </strong>{" "}
              — Access drops these from Abundance Report silently, and pasting will not
              fix them. The site is missing from VectorSurv's own sites export.
              <ul className="mt-1.5 space-y-0.5">
                {missing.slice(0, 5).map((m) => (
                  <li key={m.site_code}>
                    <code className="font-mono text-[12px]">{m.site_name}</code> (code{" "}
                    {m.site_code}) — {m.rows} row{m.rows === 1 ? "" : "s"},{" "}
                    {m.first === m.last ? m.first : `${m.first} to ${m.last}`}, week
                    {m.weeks.length > 1 ? "s" : ""} {m.weeks.join(", ")}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {dupes.length > 0 && (
            <div className="p-3 rounded-sm2 bg-sle-tint text-sle text-[13px]">
              <strong>{dupes.length} site codes have more than one active site.</strong>{" "}
              Pasting both puts two rows under one code, and the report then counts every
              collection at that site twice.
            </div>
          )}

          {inspection.readiness.collections.length > 0 && (
            <div className="ui-card p-4">
              <h3 className="font-extrabold text-[15px]">Which dates can you report on?</h3>
              <p className="text-ink-soft text-[13px] mt-1">
                A collection is entered over several days, so a date that is still
                receiving rows is not finished. Report the settled ones and pick the
                rest up next time.
              </p>

              <div className="eyebrow mt-3 mb-1.5">Ready to report</div>
              <div className="flex flex-wrap gap-1.5">
                {inspection.readiness.collections
                  .filter((c) => !c.hold)
                  .slice(0, 12)
                  .map((c) => (
                    <span key={c.date} className="pill bg-brand-tint text-brand-deep">
                      {c.date} · {c.rows.toLocaleString()}
                    </span>
                  ))}
              </div>

              {inspection.readiness.collections.some((c) => c.hold) && (
                <>
                  <div className="eyebrow mt-3 mb-1.5">
                    Not finished — hold until the next batch
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {inspection.readiness.collections
                      .filter((c) => c.hold)
                      .map((c) => (
                        <span key={c.date} className="pill bg-sle-tint text-sle">
                          {c.date} · {c.rows.toLocaleString()} so far
                        </span>
                      ))}
                  </div>
                  <p className="text-[12px] text-ink-faint mt-1.5">
                    A date is finished once two later collection dates have been
                    entered, so the newest two are held back (or any date that gained
                    rows on {inspection.readiness.latest_entry}, the latest entry day).
                  </p>
                </>
              )}

              {!inspection.readiness.ordered && inspection.readiness.overlaps[0] && (
                <p className="text-[12px] text-sle mt-3">
                  <strong>More than one collection is being entered at a time.</strong>{" "}
                  {inspection.readiness.overlaps[0].older} was still gaining rows after{" "}
                  {inspection.readiness.overlaps[0].newer} had already started. If the lab
                  finished each date before starting the next, only the newest would ever
                  need holding back.
                </p>
              )}
            </div>
          )}

          <p className="text-[13px] text-ink-soft">
            Do <strong>Surveillance Sites</strong> first, then Abundance. Both end up in
            Access either way, but a collection pasted while its site is still missing is
            dropped from the report until the site catches up — and nothing says so.
          </p>

          <Field
            label="Which table are you updating?"
            hint="Each has its own dates. Prepare and paste them one at a time."
          >
            <Segmented
              value={dataset}
              onChange={(k) => {
                setDataset(k);
                setPrepared(null);
              }}
              options={DATASETS}
            />
          </Field>

          {dataset === "abundance" ? (
            <div>
              <div className="eyebrow mb-1.5 flex flex-wrap items-center gap-2">
                <span>
                  Added to VectorSurv — newest first. Pick the days you have not pasted
                  yet.
                </span>
                {inspection.all_days.length > inspection.add_days.length && (
                  <button
                    onClick={() => {
                      const all = inspection.all_days.map((d) => d.day);
                      setAbDays(abDays.length === all.length ? [] : all);
                      setPrepared(null);
                    }}
                    className="btn-ghost text-xs !py-1 normal-case tracking-normal"
                    title="For catching Access up from scratch: every add date in the export"
                  >
                    {abDays.length === inspection.all_days.length
                      ? "Clear all"
                      : `Select all ${inspection.all_days.length} add dates`}
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {inspection.add_days.map((d) => {
                  const on = abDays.includes(d.day);
                  return (
                    <button
                      key={d.day}
                      onClick={() => {
                        setAbDays((p) =>
                          p.includes(d.day) ? p.filter((x) => x !== d.day) : [...p, d.day]
                        );
                        setPrepared(null);
                      }}
                      className={`px-3 py-2 rounded-sm2 border text-left transition ${
                        on
                          ? "border-brand bg-brand-tint text-brand-deep"
                          : "border-line hover:border-line-strong bg-card text-ink"
                      }`}
                    >
                      <div className="font-bold text-[13px]">{d.day}</div>
                      <div className="text-[12px] opacity-80">
                        {d.rows.toLocaleString()} rows
                        {d.by.length > 0 && ` · ${d.by.join(", ")}`}
                      </div>
                      <div
                        className="text-[11px] opacity-70"
                        title={d.collection_days
                          .map((c) => `${c.label}: ${c.rows} rows`)
                          .join(" · ")}
                      >
                        week{d.weeks.length > 1 ? "s" : ""} {d.weeks.join(", ")} · collected{" "}
                        {d.collection_days.map((c) => c.label).join(", ")}
                      </div>
                    </button>
                  );
                })}
              </div>
              <p className="text-[12px] text-ink-faint mt-2">
                Each row belongs to exactly one add date, so a day pasted once is never
                pasted twice. Go by the last date you ran a report for rather than waiting
                to be told VectorSurv has been updated.
              </p>

              {expected && (
                <div className="mt-3 p-3 rounded-sm2 bg-brand-tint text-brand-deep text-[13px]">
                  <strong>{expected.pasted.toLocaleString()} rows</strong> will be pasted.
                  Afterwards Access&rsquo;s Abundance table should hold{" "}
                  <strong>{expected.total.toLocaleString()} rows</strong> for this export
                  — everything collected through {expected.newest}.
                  <div className="text-[12px] opacity-80 mt-1">
                    Check the record count at the bottom of the table once you have pasted
                    (filter collection_date to 2026 if it holds earlier years). If it is
                    lower, a day was skipped or a paste did not land; if higher, rows went
                    in twice.
                  </div>
                </div>
              )}

              {nightsAvailable.length > 0 && (
                <div className="mt-4">
                  <div className="eyebrow mb-1.5">
                    Collected on — narrow to particular nights, or leave all for everything
                  </div>
                  <div className="flex flex-wrap gap-2 items-center">
                    {nightsAvailable.map((n) => {
                      const on = collectionDays.includes(n.date);
                      return (
                        <button
                          key={n.date}
                          onClick={() => {
                            setCollectionDays((p) =>
                              p.includes(n.date)
                                ? p.filter((x) => x !== n.date)
                                : [...p, n.date]
                            );
                            setPrepared(null);
                          }}
                          className={`px-3 py-1.5 rounded-sm2 border text-left transition ${
                            on
                              ? "border-brand bg-brand-tint text-brand-deep"
                              : "border-line hover:border-line-strong bg-card text-ink"
                          }`}
                        >
                          <span className="font-bold text-[13px]">{n.date}</span>
                          <span className="text-[12px] opacity-80 ml-1.5">
                            {n.rows.toLocaleString()} rows
                          </span>
                        </button>
                      );
                    })}
                    {collectionDays.length > 0 && (
                      <button
                        onClick={() => {
                          setCollectionDays([]);
                          setPrepared(null);
                        }}
                        className="btn-ghost text-xs !py-1"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <p className="text-[12px] text-ink-faint mt-2">
                    {collectionDays.length === 0 ? (
                      <>
                        Every night in the add dates above. A night keyed in across two
                        days is listed once, with both days&rsquo; rows totalled — select
                        the add dates it spans and then the night itself to get all of it.
                      </>
                    ) : (
                      <>
                        Keeping{" "}
                        {nightsAvailable
                          .filter((n) => collectionDays.includes(n.date))
                          .reduce((t, n) => t + n.rows, 0)
                          .toLocaleString()}{" "}
                        of{" "}
                        {nightsAvailable.reduce((t, n) => t + n.rows, 0).toLocaleString()}{" "}
                        rows from the chosen add dates.
                      </>
                    )}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div>
              <div className="eyebrow mb-1.5">
                Sites by date added — newest first. Deactivated rows are excluded.
              </div>
              <div className="flex flex-wrap gap-2">
                {inspection.site_days.map((d) => {
                  const on = days.includes(d.day);
                  return (
                    <button
                      key={d.day}
                      onClick={() => {
                        setDays((p) =>
                          p.includes(d.day) ? p.filter((x) => x !== d.day) : [...p, d.day]
                        );
                        setPrepared(null);
                      }}
                      className={`px-3 py-2 rounded-sm2 border text-left transition ${
                        on
                          ? "border-brand bg-brand-tint text-brand-deep"
                          : "border-line hover:border-line-strong bg-card text-ink"
                      }`}
                    >
                      <div className="font-bold text-[13px]">{d.day}</div>
                      <div className="text-[12px] opacity-80">
                        {d.active} new site{d.active === 1 ? "" : "s"}
                      </div>
                      {d.deactivated > 0 && (
                        <div className="text-[11px] opacity-70">
                          {d.deactivated} deactivated, not included
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2 items-center">
            <button
              onClick={() => prepare(false)}
              disabled={!selected || !!busy}
              className="btn-primary text-sm"
            >
              {busy === "prepare" ? "Preparing…" : "Prepare rows"}
            </button>
            <button
              onClick={() => prepare(true)}
              disabled={!selected || !!busy}
              className="btn-ghost text-sm"
            >
              {busy === "csv" ? "Building…" : "Download CSV"}
            </button>
            {!selected && (
              <span className="text-[12px] text-ink-faint">
                Pick at least one date.
              </span>
            )}
          </div>
        </>
      )}

      {prepared && (
        <div className="ui-card p-4">
          <div className="flex flex-wrap items-baseline gap-2">
            <h3 className="font-extrabold text-[15px]">
              {prepared.rows.length.toLocaleString()} rows for [{prepared.table}]
            </h3>
            <button onClick={copy} className="btn-primary text-sm ml-auto !py-1.5">
              {copied ? "Copied" : "Copy for Access"}
            </button>
          </div>
          <p className="text-ink-soft text-[13px] mt-1">
            Click the first cell of the empty row at the bottom of [{prepared.table}] and
            paste. No header row is copied — it would become a record.
            {prepared.unfilled.length > 0 && (
              <>
                {" "}
                Access keeps one more column,{" "}
                <code className="font-mono text-[12px]">
                  {prepared.unfilled.join(", ")}
                </code>
                , which this paste does not fill.
              </>
            )}
          </p>
          {prepared.skipped.length > 0 && (
            <p className="text-[12px] text-ink-faint mt-1">
              {prepared.skipped.length} deactivated site
              {prepared.skipped.length === 1 ? "" : "s"} left out.
            </p>
          )}
          <div className="overflow-auto border border-line rounded-sm2 mt-3 max-h-72">
            <table className="w-full text-[12px]">
              <thead className="bg-paper-2 sticky top-0">
                <tr>
                  {prepared.columns.map((c) => (
                    <th key={c} className="px-2 py-1.5 text-left whitespace-nowrap font-semibold">
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {prepared.rows.slice(0, 15).map((r, i) => (
                  <tr key={i} className="border-t border-line">
                    {r.map((v, j) => (
                      <td key={j} className="px-2 py-1 whitespace-nowrap max-w-[16rem] truncate">
                        {v}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {prepared.rows.length > 15 && (
            <p className="text-[12px] text-ink-faint mt-1.5">
              Showing the first 15. All {prepared.rows.length.toLocaleString()} are copied.
            </p>
          )}
        </div>
      )}

      <div className="ui-card p-4">
        <div className="eyebrow mb-3">Abundance report watcher</div>
        <AbundanceWatchStatus />
      </div>
    </div>
  );
}
